(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-wi-community-press]';
  const instances = new WeakMap();

  class WICommunityPress {
    constructor(root) {
      this.root = root;
      this.mediaSlides = [...root.querySelectorAll('[data-wi-cp-media]')];
      this.cards = [...root.querySelectorAll('[data-wi-cp-card]')];
      this.prevButton = root.querySelector('[data-wi-cp-prev]');
      this.nextButton = root.querySelector('[data-wi-cp-next]');
      this.progress = root.querySelector('[data-wi-cp-progress]');
      this.progressBar = root.querySelector('[data-wi-cp-progress-bar]');
      this.currentCounters = [...root.querySelectorAll('[data-wi-cp-current]')];
      this.status = root.querySelector('[data-wi-cp-status]');
      this.autoplayToggle = root.querySelector('[data-wi-cp-autoplay-toggle]');
      this.swipeAreas = [...root.querySelectorAll('[data-wi-cp-swipe]')];
      this.videos = [...root.querySelectorAll('.wi-cp__media-video')];

      this.count = Math.min(this.mediaSlides.length, this.cards.length);
      this.index = 0;
      this.loop = root.dataset.loop === 'true';
      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.pauseOnHover = root.dataset.pauseHover === 'true';
      this.autoplaySpeed = Math.max(Number(root.dataset.autoplaySpeed) || 5000, 2000);

      this.abortController = new AbortController();
      this.timer = 0;
      this.startX = 0;
      this.startY = 0;
      this.pointerActive = false;
      this.isSwiping = false;
      this.isVisible = true;
      this.isHovering = false;
      this.hasFocus = false;
      this.userStoppedAutoplay = false;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.reducedMotionQuery.matches;
      this.labelPauseAutoplay = root.dataset.labelPauseAutoplay || 'Pause autoplay';
      this.labelPlayAutoplay = root.dataset.labelPlayAutoplay || 'Play autoplay';

      if (this.count < 1) return;

      this.bindEvents();
      this.observeVisibility();
      this.update(false);
      this.syncAutoplayToggle();
      this.scheduleAutoplay();
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.prevButton?.addEventListener('click', () => this.goTo(this.index - 1, true), { signal });
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1, true), { signal });

      this.swipeAreas.forEach((area) => {
        area.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
        area.addEventListener('pointerdown', (event) => this.onPointerDown(event), { passive: true, signal });
        area.addEventListener('pointerup', (event) => this.onPointerUp(event), { passive: true, signal });
        area.addEventListener('pointercancel', () => {
          this.pointerActive = false;
          this.isSwiping = false;
        }, { passive: true, signal });

        area.addEventListener('click', (event) => {
          if (this.isSwiping) {
            event.preventDefault();
            event.stopPropagation();
            this.isSwiping = false;
          }
        }, { capture: true, signal });
      });

      this.autoplayToggle?.addEventListener('click', () => {
        this.userStoppedAutoplay = !this.userStoppedAutoplay;
        if (this.userStoppedAutoplay) this.clearAutoplay();
        else this.scheduleAutoplay();
        this.syncAutoplayToggle();
      }, { signal });

      this.root.addEventListener('mouseenter', () => {
        this.isHovering = true;
        if (this.pauseOnHover) this.clearAutoplay();
      }, { signal });

      this.root.addEventListener('mouseleave', () => {
        this.isHovering = false;
        this.scheduleAutoplay();
      }, { signal });

      this.root.addEventListener('focusin', () => {
        this.hasFocus = true;
        this.clearAutoplay();
      }, { signal });

      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) {
          this.hasFocus = false;
          this.scheduleAutoplay();
        }
      }, { signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.clearAutoplay();
          this.pauseVideos();
        } else {
          this.updateVideoState();
          this.scheduleAutoplay();
        }
      }, { signal });

      const onMotionChange = (event) => {
        this.reducedMotion = event.matches;
        if (this.reducedMotion) {
          this.clearAutoplay();
          this.pauseVideos();
        } else {
          this.updateVideoState();
          this.scheduleAutoplay();
        }
        this.syncAutoplayToggle();
      };

      if (this.reducedMotionQuery.addEventListener) {
        this.reducedMotionQuery.addEventListener('change', onMotionChange, { signal });
      } else {
        this.reducedMotionQuery.addListener(onMotionChange);
        signal.addEventListener('abort', () => {
          this.reducedMotionQuery.removeListener(onMotionChange);
        }, { once: true });
      }
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.isVisible = entries[0]?.isIntersecting ?? true;

        if (!this.isVisible) {
          this.clearAutoplay();
          this.pauseVideos();
        } else {
          this.updateVideoState();
          this.scheduleAutoplay();
        }
      }, { threshold: 0.05 });

      this.intersectionObserver.observe(this.root);
    }

    onKeyDown(event) {
      if (event.target.closest('video, input, textarea, select')) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1, true);
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse') return;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.pointerActive = true;
      this.isSwiping = false;
    }

    onPointerUp(event) {
      if (!this.pointerActive) return;
      this.pointerActive = false;

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;

      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }

      this.isSwiping = true;
      this.goTo(deltaX < 0 ? this.index + 1 : this.index - 1, true);

      window.setTimeout(() => {
        this.isSwiping = false;
      }, 300);
    }

    normalizeIndex(index) {
      if (this.count <= 1) return 0;
      if (this.loop) return ((index % this.count) + this.count) % this.count;
      return Math.min(Math.max(index, 0), this.count - 1);
    }

    goTo(index, userInitiated = false) {
      const next = this.normalizeIndex(index);

      if (next === this.index) {
        if (userInitiated) this.restartAutoplay();
        return;
      }

      this.index = next;
      this.update(true);

      if (userInitiated) this.restartAutoplay();
    }

    update(announce = true) {
      const nextIndex = this.count > 1
        ? (this.index + 1) % this.count
        : this.index;

      this.mediaSlides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');

        slide.querySelectorAll('a, button, input, select, textarea, video[controls]').forEach((element) => {
          if (active) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', '-1');
        });
      });

      this.cards.forEach((card, cardIndex) => {
        const current = cardIndex === this.index;
        const next = this.count > 1 && cardIndex === nextIndex;

        card.classList.toggle('is-current', current);
        card.classList.toggle('is-next', next && !current);
        card.setAttribute('aria-hidden', current || next ? 'false' : 'true');

        card.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          if (current || next) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', '-1');
        });
      });

      this.currentCounters.forEach((counter) => {
        counter.textContent = String(this.index + 1);
      });

      this.progress?.setAttribute('aria-valuenow', String(this.index + 1));

      if (this.progressBar) {
        this.progressBar.style.transform = `scaleX(${(this.index + 1) / this.count})`;
      }

      if (this.prevButton) {
        this.prevButton.disabled = this.count < 2 || (!this.loop && this.index === 0);
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.count < 2 || (!this.loop && this.index === this.count - 1);
      }

      this.updateVideoState();

      if (announce && this.status) {
        const title = this.cards[this.index]
          ?.querySelector('.wi-cp__article-title')
          ?.textContent
          ?.trim();

        const index = String(this.index + 1);
        const count = String(this.count);
        const plainTemplate = this.root.dataset.labelSlideStatus || 'Slide __INDEX__ of __COUNT__';
        const titleTemplate = this.root.dataset.labelSlideStatusTitle || plainTemplate;
        this.status.textContent = (title ? titleTemplate : plainTemplate)
          .replace(/__INDEX__/g, index)
          .replace(/__COUNT__/g, count)
          .replace(/__TITLE__/g, title || '');
      }
    }

    updateVideoState() {
      this.videos.forEach((video) => {
        const slide = video.closest('[data-wi-cp-media]');
        const active = slide?.classList.contains('is-active');

        if (!active || !this.isVisible || document.hidden) {
          video.pause();
          return;
        }

        if (!this.reducedMotion) {
          // Muted + playsInline keeps autoplay compatible with modern browser policies.
          video.muted = true;
          video.playsInline = true;

          const promise = video.play();
          promise?.catch?.((error) => {
            // Native controls remain available if a browser blocks autoplay.
            if (window.Shopify?.designMode) {
              console.warn('[WI Community & Press] Video autoplay was blocked.', error);
            }
          });
        }
      });
    }

    pauseVideos() {
      this.videos.forEach((video) => video.pause());
    }

    syncAutoplayToggle() {
      if (!this.autoplayToggle) return;

      this.autoplayToggle.hidden = !this.autoplayEnabled;
      const paused = this.userStoppedAutoplay || this.reducedMotion;
      this.autoplayToggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.autoplayToggle.setAttribute(
        'aria-label',
        paused ? this.labelPlayAutoplay : this.labelPauseAutoplay
      );
      this.autoplayToggle.disabled = this.reducedMotion;
    }

    canAutoplay() {
      return (
        this.autoplayEnabled &&
        !this.userStoppedAutoplay &&
        !this.reducedMotion &&
        this.count > 1 &&
        this.isVisible &&
        !document.hidden &&
        !this.hasFocus &&
        !(this.pauseOnHover && this.isHovering)
      );
    }

    scheduleAutoplay() {
      if (this.timer || !this.canAutoplay()) return;

      if (!this.loop && this.index >= this.count - 1) return;

      this.timer = window.setTimeout(() => {
        this.timer = 0;
        this.goTo(this.index + 1, false);
        this.scheduleAutoplay();
      }, this.autoplaySpeed);
    }

    clearAutoplay() {
      if (!this.timer) return;
      window.clearTimeout(this.timer);
      this.timer = 0;
    }

    restartAutoplay() {
      this.clearAutoplay();
      this.scheduleAutoplay();
    }

    destroy() {
      this.clearAutoplay();
      this.pauseVideos();
      this.intersectionObserver?.disconnect();
      this.abortController.abort();
    }
  }

  const initialize = (scope = document) => {
    const roots = [];

    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(ROOT_SELECTOR) || []));

    roots.forEach((root) => {
      if (instances.has(root)) return;

      const instance = new WICommunityPress(root);
      if (instance.count > 0) instances.set(root, instance);
    });
  };

  const destroyWithin = (scope) => {
    const roots = [];

    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(ROOT_SELECTOR) || []));

    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));

  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target?.closest?.(ROOT_SELECTOR) || document.getElementById(`WICommunityPress-${event.detail?.sectionId}`);
    const instance = root ? instances.get(root) : null;
    if (!instance) return;

    const targetBlockId = event.detail?.blockId || event.target?.dataset?.blockId;
    if (!targetBlockId) return;

    const targetSlide = instance.mediaSlides.find((s) => s.dataset.blockId === targetBlockId);
    if (!targetSlide) return;

    const index = Number(targetSlide.dataset.index);
    if (Number.isInteger(index)) instance.goTo(index, false);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target?.closest?.(ROOT_SELECTOR) || document.getElementById(`WICommunityPress-${event.detail?.sectionId}`);
    const instance = root ? instances.get(root) : null;
    if (instance) instance.restartAutoplay();
  });
})();